import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@percent/lemonade'
import { useVolunteeringActionsDialogs } from '../volunteeringActionsDialogs/VolunteeringActionsDialogsProvider'
import { useServices } from '@percent/cause-dashboard/context/serviceContext/ServiceContext'
import { APIErrorHandler } from '@percent/cause-dashboard/common/library/APIErrorHandler'
import { AxiosError } from 'axios'

export type UseVolunteeringActionsProps = Readonly<{
  opportunityId: string
  opportunityName: string
}>

export type UseVolunteeringActionsReturn = Readonly<{
  editAttendance: () => void
  exportAttendees: () => Promise<void>
  isExporting: boolean
  cancelOpportunity: () => void
  editOpportunity: () => void
}>

export const useVolunteeringActions = ({
  opportunityId,
  opportunityName
}: UseVolunteeringActionsProps): UseVolunteeringActionsReturn => {
  const { t } = useTranslation()
  const { addToast } = useToast()
  const { volunteeringService } = useServices()
  const navigate = useNavigate()
  const { editAttendance: openEditAttendanceModal, cancelOpportunity: openCancelOpportunityModal } =
    useVolunteeringActionsDialogs()
  const [isExporting, setIsExporting] = useState(false)

  const editAttendance = useCallback(() => {
    openEditAttendanceModal({ id: opportunityId, name: opportunityName })
  }, [opportunityId, opportunityName, openEditAttendanceModal])

  const exportAttendees = useCallback(async () => {
    if (isExporting) return

    setIsExporting(true)
    try {
      const blob = await volunteeringService.getAttendeesReport(opportunityId)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', t('volunteering.exportAttendees.attendeesFileName', { opportunityName }))
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
      addToast(t('volunteering.exportAttendees.successToast'), 'success')
    } catch (err: unknown) {
      const errorMessage =
        err instanceof AxiosError
          ? APIErrorHandler(err.response?.data?.error)
          : t('volunteering.exportAttendees.errorFallback')
      addToast(errorMessage, 'error')
    } finally {
      setIsExporting(false)
    }
  }, [opportunityId, opportunityName, isExporting, volunteeringService, addToast, t])

  const cancelOpportunity = useCallback(() => {
    openCancelOpportunityModal({ id: opportunityId, name: opportunityName })
  }, [opportunityId, opportunityName, openCancelOpportunityModal])

  const editOpportunity = useCallback(() => {
    navigate(`/volunteering/opportunities/${opportunityId}/edit`)
  }, [opportunityId, navigate])

  return {
    editAttendance,
    exportAttendees,
    isExporting,
    cancelOpportunity,
    editOpportunity
  }
}
